// Etiqueta logística GS1 en ZPL NATIVO (Zebra GK420t y compatibles, 203 dpi).
//
// Por qué existe: imprimir la etiqueta HTML desde el navegador rasteriza los
// códigos de barras como imagen (anti-aliasing + módulos de ancho fraccionario
// a 203 dpi → barras dentadas/moteadas). En ZPL la IMPRESORA dibuja las barras
// con precisión exacta de dots — el formato con el que un verificador GS1
// mide mejor. Se imprime saltándose el driver:  lp -d <cola> -o raw archivo.zpl
//
// Sin dependencias (no usa bwip-js): el código de barras lo genera la Zebra.

const LABEL_W = 800   // 100 mm de ancho REAL de la etiqueta (no los 4,09" del cabezal)
const LABEL_H = 800   // 100 mm de alto (etiqueta 10×10 cm)

// Campo ^FD seguro: sin caracteres de control ZPL
const esc = s => String(s || '').replace(/[\^~>\\]/g, ' ').trim()

const FNC1 = '\u001d'

// ─────────────────────────────────────────────────────────────────────────
// PERFILES ZPL por familia de impresora. Las Zebra "modernas" (GK420t, GX,
// ZD, ZT — firmware V60/V50/V53 x.14+) aceptan ^CI28 (UTF-8) y el escape `_`
// en DataMatrix. Las de la familia 2844-Z / 2824-Z / 3842-Z (firmware V45,
// ej. TLP2844-Z V45.11.6ZA) son ZPL II "clásico": sin UTF-8 (imprimirían
// basura por cada tilde) y el escape por defecto de ^BX es `~`.
// ─────────────────────────────────────────────────────────────────────────
export const ZPL_PROFILES = {
  modern: { id: 'modern', label: 'ZPL moderno', utf8: true, dmEscape: '_', darkness: 18 },
  // darkness 22: la 2844-Z (transferencia térmica) imprimía gris/desparejo a 18
  legacy: { id: 'legacy', label: 'ZPL clásico (2844-Z): sin tildes', utf8: false, dmEscape: '~', darkness: 22 }
}
// Con escape `~` el analizador ZPL trata cada `~` del ^FD como PREFIJO DE
// COMANDO y se come parte de los datos (visto en la TLP2844-Z: DataMatrix
// diminuto con el contenido recortado). Solución estándar de Zebra: ^CT
// cambia el prefijo de control durante el formato para que el `~` sea dato.
const CT_TEMP = '|'

/**
 * Tamaño (lado, en módulos) del Data Matrix ECC200 cuadrado más chico que
 * contiene los datos, estimando la codificación ASCII que usa ZPL: par de
 * dígitos = 1 codeword, cualquier otro carácter (y FNC1) = 1 codeword.
 * Sirve para centrar el símbolo y reservar el alto justo.
 */
export function dataMatrixSide(data) {
  let cw = 0
  const str = String(data || '')
  for (let i = 0; i < str.length; i++) {
    if (/\d/.test(str[i]) && /\d/.test(str[i + 1] || '')) { cw++; i++ } else cw++
  }
  const CAP = [[10, 3], [12, 5], [14, 8], [16, 12], [18, 18], [20, 22], [22, 30], [24, 36], [26, 44],
    [32, 62], [36, 86], [40, 114], [44, 144], [48, 174], [52, 204], [64, 280], [72, 368], [80, 456],
    [88, 576], [96, 696], [104, 816], [120, 1050], [132, 1304], [144, 1558]]
  const hit = CAP.find(([, c]) => c >= cw)
  return hit ? hit[0] : 144
}


/** Perfil según el modelo que reporta Browser Print (uid/nombre). */
export function profileForPrinter(device) {
  const s = `${(device && device.uid) || ''} ${(device && device.name) || ''}`
  return /2844|2824|3842|3844|888/i.test(s) ? ZPL_PROFILES.legacy : ZPL_PROFILES.modern
}

/** Quita tildes/diéresis (é→e, ñ→n) y descarta lo que no sea ASCII
 *  imprimible: lo que entiende una impresora sin UTF-8 con su code page. */
export function stripDiacritics(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x20-\x7e]/g, '')
}

// Texto para ^FD según el perfil (escape ZPL + transliteración si no hay UTF-8)
const fieldText = (s, profile) => profile.utf8 ? esc(s) : stripDiacritics(esc(s))

/**
 * Codifica los datos (con FNC1 = \u001d) para ^BC en modo N con SUBSETS
 * EXPLICITOS — en modo N el subset por defecto de ZPL es el B (1 caracter =
 * 1 codeword): sin >; el simbolo sale casi al doble de ancho (bug detectado
 * en la GK420t: el SSCC se salia de la etiqueta).
 *  - Datos 100% numericos → subset C (>;) con pares; un digito suelto final
 *    se emite en B (>:) porque C exige pares.
 *  - Datos con letras → subset B completo (>:).
 * FNC1 = >8 en ambos casos.
 */
export function zplBcField(data) {
  const s = String(data)
  let out = ''
  let subset = null // null = aún sin start; 'B' | 'C'
  const enter = sub => {
    if (subset === sub) return
    if (subset === null) out += (sub === 'C' ? '>;' : '>:')      // códigos de INICIO
    else out += (sub === 'C' ? '>5' : '>6')                       // cambios mid-símbolo
    subset = sub
  }
  const digitRunAt = k => {
    let j = k
    while (j < s.length && s[j] >= '0' && s[j] <= '9') j++
    return j - k
  }
  let i = 0
  while (i < s.length) {
    if (s[i] === FNC1) {
      // el start lo decide lo que VIENE después del FNC1
      if (subset === null) enter(digitRunAt(i + 1) >= 6 ? 'C' : 'B')
      out += '>8'
      i++
      continue
    }
    const run = digitRunAt(i)
    if (run >= 6) {
      const pairs = Math.floor(run / 2)
      enter('C')
      out += s.slice(i, i + pairs * 2)
      i += pairs * 2
      if (run % 2 === 1) { enter('B'); out += s[i]; i++ }
    } else {
      enter('B')
      const j = i + Math.max(run, 1)
      out += s.slice(i, run > 0 ? j : i + 1)
      i = run > 0 ? j : i + 1
    }
  }
  return out
}

/**
 * Estima el ancho en dots de un Code 128 codificado con las reglas de
 * zplBcField (C para runs de >=6 dígitos, B para el resto), incluyendo
 * start + check + stop. Espejo del encoder — mantener en sintonía.
 */
export function code128WidthDots(data, moduleDots) {
  const s = String(data)
  let cws = 0
  let subset = null
  const enter = sub => { if (subset !== sub) { if (subset !== null) cws += 1; subset = sub } }
  let i = 0
  while (i < s.length) {
    if (s[i] === FNC1) { cws += 1; i++; continue }
    let j = i
    while (j < s.length && s[j] >= '0' && s[j] <= '9') j++
    const run = j - i
    if (run >= 6) {
      enter('C')
      cws += Math.floor(run / 2)
      i += Math.floor(run / 2) * 2
      if (run % 2 === 1) { enter('B'); cws += 1; i++ }
    } else {
      enter('B')
      cws += Math.max(run, 1)
      i += Math.max(run, 1)
    }
  }
  // + start + check; stop = 13 módulos
  return (11 * (cws + 2) + 13) * moduleDots
}

/**
 * Construye la etiqueta logística en ZPL desde el JSON del packing list
 * (mismo formato que buildLogisticLabelHTML). extraAI = { ai: '90'..'99',
 * value } opcional.
 * Devuelve el texto ZPL listo para guardar como .zpl e imprimir en crudo.
 */
/**
 * Vista "monoproducto" del pallet: todas las líneas son el MISMO GTIN (pueden
 * ser varios lotes — el egreso divide la línea por lote consumido). Devuelve
 * un item consolidado (cantidad sumada; lote/vencimiento solo si son únicos
 * en todo el pallet) o null si el pallet es mixto.
 */
export function monoItem(items) {
  if (!items || !items.length) return null
  const gtins = new Set(items.map(i => i.gtin14).filter(Boolean))
  if (gtins.size !== 1 || !items.every(i => i.gtin14)) return null
  const unique = key => {
    const vals = items.map(i => i[key])
    if (!vals.every(Boolean)) return null
    const u = new Set(vals.map(v => String(v).slice(0, 10)))
    return u.size === 1 ? vals[0] : null
  }
  return {
    ...items[0],
    quantity: items.reduce((s, i) => s + (Number(i.quantity) || 0), 0),
    lot_number: unique('lot_number'),
    expiry_date: unique('expiry_date')
  }
}

/**
 * AIs del GS1-128 de CONTENIDO de la etiqueta logística (solo pallet
 * monoproducto): (02) GTIN del contenido, (17) vencimiento, (3102) peso,
 * (37) cantidad, (10) lote, (9x) uso interno. Orden natural: fijos primero.
 * Cada parte: { ai, code (sin paréntesis), human (con paréntesis), variable }.
 */
export function logisticContentParts(packingList, extraAI = null) {
  const sh = packingList.shipment || {}
  const it = monoItem(packingList.items || [])
  if (!it) return []
  const content = sh.content || null
  const contentG14 = (content && content.gtin14) || it.gtin14 || null
  if (!contentG14) return []
  const contentCount = (content && content.count) || it.quantity || 1
  const netKg = sh.net_weight_kg
  const parts = [{ ai: '02', code: `02${contentG14}`, human: `(02)${contentG14}`, variable: false }]
  const venc = it.expiry_date ? String(it.expiry_date).slice(0, 10) : null
  const m = venc && venc.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) {
    const d = `${m[1].slice(2)}${m[2]}${m[3]}`
    parts.push({ ai: '17', code: `17${d}`, human: `(17)${d}`, variable: false })
  }
  if (netKg && netKg <= 9999.99) {
    const w6 = String(Math.round(netKg * 100)).padStart(6, '0')
    parts.push({ ai: '3102', code: `3102${w6}`, human: `(3102)${w6}`, variable: false })
  }
  parts.push({ ai: '37', code: `37${String(contentCount)}`, human: `(37)${contentCount}`, variable: true })
  if (it.lot_number) {
    const lot = esc(it.lot_number)
    parts.push({ ai: '10', code: `10${lot}`, human: `(10)${lot}`, variable: true })
  }
  const extraOk = extraAI && /^9[0-9]$/.test(String(extraAI.ai)) && extraAI.value
  if (extraOk) {
    const v = esc(String(extraAI.value).slice(0, 30))
    parts.push({ ai: String(extraAI.ai), code: `${extraAI.ai}${v}`, human: `(${extraAI.ai})${v}`, variable: true })
  }
  return parts
}

/** Solo los AIs (orden natural) — para los chips de reordenamiento. */
export const logisticLabelAIs = (packingList, extraAI = null) => logisticContentParts(packingList, extraAI).map(p => p.ai)

/** Reordena según `order` (lista de AIs); los no listados quedan al final en
 *  su orden relativo. GS1 no exige orden de AIs. */
export function orderAIs(list, order, key = x => x) {
  if (!order || !order.length) return list
  const rank = ai => { const i = order.indexOf(ai); return i < 0 ? order.length : i }
  return list.map((p, i) => ({ p, i })).sort((a, b) => rank(key(a.p)) - rank(key(b.p)) || a.i - b.i).map(x => x.p)
}

// Data de un grupo: FNC1 inicial + AIs, con FNC1 tras cada variable no final
const groupData = group => FNC1 + group
  .map((p, i) => p.code + (p.variable && i < group.length - 1 ? FNC1 : ''))
  .join('')

/**
 * Reparte las partes en hasta `maxGroups` GS1-128 (empaquetado codicioso):
 * cada símbolo respeta `maxChars` caracteres del texto legible CON AIs y
 * paréntesis (regla de la casa: 48, el máximo de datos GS1-128) y, si se da
 * `maxWidthDots`, también el ancho a `module` dots. Un AI variable que no
 * queda al final de su símbolo va seguido de FNC1 (lo resuelve groupData).
 */
export function splitLogisticGroups(parts, { maxChars = 48, maxWidthDots = null, module = 2, maxGroups = 2 } = {}) {
  const humanLen = g => g.reduce((n, p) => n + p.human.length, 0)
  const tooWide = g => maxWidthDots != null && code128WidthDots(groupData(g), module) > maxWidthDots
  const groups = []
  let current = []
  for (const p of parts) {
    const next = [...current, p]
    if (current.length && (humanLen(next) > maxChars || tooWide(next))) {
      groups.push(current)
      current = []
      if (groups.length === maxGroups) break // presupuesto vertical de la etiqueta
    }
    current.push(p)
  }
  if (current.length && groups.length < maxGroups) groups.push(current)
  return groups
}

export function buildLogisticLabelZPL(packingList, extraAI = null, profile = ZPL_PROFILES.modern, options = {}) {
  const T = v => fieldText(v, profile)
  const aiOrder = options.aiOrder || []
  const maxChars = options.maxChars || 48
  const sh = packingList.shipment || {}
  const items = packingList.items || []
  const co = packingList.company || {}
  const it = monoItem(items)
  const mono = !!it
  const hasSscc = !!sh.sscc
  const internalRef = esc(sh.order_number || sh.picking_list_number || '')
  // Contenido declarado de la unidad logística (checklist 4.2): si el pallet
  // se armó por CAJAS, el (02) es el GTIN de la caja y el (37) el n° de cajas
  const content = sh.content || null
  const byCase = !!(content && content.level === 'case')
  const contentG14 = (content && content.gtin14) || (it && it.gtin14) || null
  const contentCount = (content && content.count) || (it && it.quantity) || 1

  const z = []
  const text = (x, y, size, value, bold = false) => {
    // "bold" en fuente escalable: mismo alto con más ancho de trazo
    z.push(`^FO${x},${y}^A0N,${size},${bold ? Math.round(size * 1.05) : size}^FD${T(value)}^FS`)
  }
  const hline = y => z.push(`^FO8,${y}^GB796,2,2^FS`)
  // Texto centrado al ancho completo (para las líneas HRI bajo cada símbolo)
  const centeredText2 = (y, size, value, bold = false) =>
    z.push(`^FO0,${y}^FB${LABEL_W},1,0,C,0^A0N,${size},${bold ? Math.round(size * 1.05) : size}^FD${T(value)}^FS`)

  z.push(`~SD${profile.darkness || 18}`)   // oscuridad /30 según perfil (ajustable si sale claro/oscuro)
  z.push('^XA')
  if (profile.utf8) z.push('^CI28')   // UTF-8 (acentos) — no en ZPL clásico (2844-Z)
  z.push('^PR2')           // 2 ips: velocidad baja = barras más nítidas
  z.push(`^PW${LABEL_W}`)
  z.push(`^LL${LABEL_H}`)
  z.push('^LH0,0')

  // Marco
  z.push(`^FO8,8^GB796,784,2^FS`)

  // ── Sección 1: emisor ──
  let y = 22
  text(24, y, 30, co.name || 'Empresa emisora', true); y += 36
  const dir = [co.address, co.city, co.country].filter(Boolean).join(' - ')
  if (dir) { text(24, y, 18, dir); y += 22 }
  const contacto = [co.phone && `Fono: ${co.phone}`, co.website].filter(Boolean).join(' · ')
  if (contacto) { text(24, y, 18, contacto); y += 22 }
  y += 4
  hline(y); y += 12

  // ── Sección 2: datos visuales ──
  if (mono) {
    text(24, y, 24, `Producto: ${it.description || ''}`); y += 30
  } else {
    text(24, y, 24, 'Producto: Productos Varios'); y += 30
  }
  if (hasSscc) {
    text(24, y, 26, `SSCC: ${sh.sscc}`, true); y += 32
  } else if (internalRef) {
    text(24, y, 26, `Ref. interna: ${internalRef}`, true); y += 32
  }
  if (mono) {
    if (byCase) {
      text(24, y, 22, `GTIN caja: ${contentG14}   Lote: ${it.lot_number || '-'}`); y += 28
      const venc = it.expiry_date ? String(it.expiry_date).slice(0, 10) : '-'
      text(24, y, 22, `Vencimiento: ${venc}   Cant: ${contentCount} cajas (${it.quantity} un.)`); y += 28
    } else {
      text(24, y, 22, `GTIN: ${it.gtin14 || ''}   Lote: ${it.lot_number || '-'}`); y += 28
      const venc = it.expiry_date ? String(it.expiry_date).slice(0, 10) : '-'
      text(24, y, 22, `Vencimiento: ${venc}   Cant: ${it.quantity}`); y += 28
    }
  }
  const netKg = sh.net_weight_kg
  if (netKg) {
    text(24, y, 22, `Peso neto: ${netKg} kg   Peso bruto: ${sh.gross_weight_kg || netKg} kg`); y += 28
  }
  const extraOk = extraAI && /^9[0-9]$/.test(String(extraAI.ai)) && extraAI.value
  const extraValue = extraOk ? esc(String(extraAI.value).slice(0, 30)) : null
  if (extraOk) {
    text(24, y, 22, `Uso interno: ${extraValue}`); y += 28
  }
  const destino = [sh.customer_name, sh.order_number, sh.date].filter(Boolean).join(' - ')
  if (destino) { text(24, y, 18, `Destinatario: ${destino}`); y += 24 }
  y += 2
  hline(y); y += 14

  // ── Sección 3: códigos de barras (los dibuja la impresora) ──
  // Contenido (solo monoproducto): a 203 dpi y 4" de ancho, un element string
  // completo NO cabe en un solo símbolo con módulo legible → se reparte en
  // HASTA DOS GS1-128 apilados (práctica estándar de la etiqueta logística
  // GS1): los AIs fijos (02)(17)(3102) tienden al primero y los variables
  // (37)(10)(9x) al segundo, con FNC1 tras cada AI variable no final.
  if (mono && hasSscc && contentG14) {
    // (02) = GTIN del contenido: la CAJA si el pallet se armó por unidades de
    // despacho (checklist 4.2), el producto si se armó por unidades; el (37)
    // cuenta en la misma unidad que declara el (02)
    // Partes en el orden pedido (chips del diálogo) y repartidas en hasta 2
    // símbolos: máx. `maxChars` (48) caracteres legibles con AIs y paréntesis
    // por GS1-128, y que quepan a módulo 2 en el ancho de la etiqueta
    const parts = orderAIs(logisticContentParts(packingList, extraAI), aiOrder, p => p.ai)
    const groups = splitLogisticGroups(parts, { maxChars, maxWidthDots: LABEL_W - 60, module: 2, maxGroups: 2 })

    for (const group of groups) {
      // Centrado horizontal (observación del revisor GS1: un símbolo pegado
      // al borde se confunde con el margen de la etiqueta)
      const data = groupData(group)
      const bx = Math.max(20, Math.round((LABEL_W - code128WidthDots(data, 2)) / 2))
      z.push(`^BY2,3,90`)
      z.push(`^FO${bx},${y}^BCN,90,N,N,N^FD${zplBcField(data)}^FS`)
      y += 98
      centeredText2(y, 18, group.map(p => p.human).join('')); y += 24
    }
    y += 4
  }

  // SSCC (00) — la identificación primaria, en grande
  if (hasSscc) {
    const ssccData = `${FNC1}00${sh.sscc}`
    // (00)+18 dígitos en subset C ≈ 156 módulos: módulo 4 dots = X 0,50 mm
    // (el mínimo recomendado GS1 para etiqueta logística) y cabe en 812
    const module = code128WidthDots(ssccData, 4) <= LABEL_W - 60 ? 4 : 3
    const bx = Math.max(20, Math.round((LABEL_W - code128WidthDots(ssccData, module)) / 2))
    z.push(`^BY${module},3,150`)
    z.push(`^FO${bx},${y}^BCN,150,N,N,N^FD${zplBcField(ssccData)}^FS`)
    y += 158
    centeredText2(y, 22, `(00) ${sh.sscc}`, true)
  } else if (internalRef) {
    // Referencia interna en subset B puro (el ^FD va crudo, sin zplBcField):
    // ancho = (start + chars + check) * 11 + stop(13), a módulo 3
    const refW = (internalRef.length + 2) * 11 * 3 + 13 * 3
    const bx = Math.max(20, Math.round((LABEL_W - refW) / 2))
    z.push(`^BY3,3,150`)
    z.push(`^FO${bx},${y}^BCN,150,N,N,N^FD${internalRef}^FS`)
    y += 158
    centeredText2(y, 18, `${internalRef} - REFERENCIA INTERNA (NO ES CLAVE GS1)`)
  }

  z.push('^XZ')
  return z.join('\n')
}


// ─────────────────────────────────────────────────────────────────────────
// Impresión de SIMBOLOGÍAS individuales en ZPL nativo (modal de Ingresos):
// la Zebra dibuja el símbolo exacto por dot — calidad de verificación.
// Formato: símbolo centrado, sin bordes (salvo las barras portadoras que el
// estándar ITF-14 exige), nombre del producto y GTIN como texto.
// ─────────────────────────────────────────────────────────────────────────

// AIs de largo FIJO (no llevan FNC1 detrás); el resto son variables
const FIXED_AI_LEN = {
  '00': 18, '01': 14, '02': 14, '11': 6, '13': 6, '15': 6, '17': 6,
  '20': 2, '402': 17, '410': 13, '411': 13, '412': 13, '413': 13,
  '414': 13, '415': 13, '416': 13, '417': 13, '8018': 18
}
const isFixedAI = ai => ai in FIXED_AI_LEN || /^3[1-6]\d\d$/.test(ai)

/**
 * Convierte un element string con paréntesis "(01)078...(10)LOTE(91)X" al
 * stream con FNC1 (GS) tras cada AI VARIABLE no final — el formato que
 * consumen los comandos de barcode ZPL.
 */
export function parenToFNC1(elementString) {
  const parts = String(elementString || '').split(/\((\d{2,4})\)/).filter(Boolean)
  let out = ''
  for (let i = 0; i < parts.length; i += 2) {
    const ai = parts[i]
    const value = parts[i + 1] || ''
    out += ai + value
    const isLast = i + 2 >= parts.length
    if (!isFixedAI(ai) && !isLast) out += FNC1
  }
  return out
}

// Texto centrado con field block (ancho completo de la etiqueta)
const centeredText = (y, size, value, bold = false) =>
  `^FO0,${y}^FB800,1,0,C,0^A0N,${size},${bold ? Math.round(size * 1.05) : size}^FD${esc(value)}^FS`

/**
 * ZPL de UNA simbología del modal de impresión.
 * payload: { productName, gtin, elementString (con paréntesis, para las
 * simbologías GS1 con AIs), upcaExpansion (para UPC-E), quantity }
 * Devuelve null si la simbología no tiene soporte ZPL (GS1 QR).
 */
export function buildSymbologyPrintZPL(symbology, payload, profile = ZPL_PROFILES.modern) {
  const T = v => fieldText(v, profile)
  const raw = String(payload.gtin || '').replace(/\D/g, '')
  const upcaExp = String(payload.upcaExpansion || '').replace(/\D/g, '')
  // UPC-E: el GTIN de generación es su UPC-A expandido (payload.upcaExpansion).
  // Rellenar el UPC-E crudo con ceros daría un GTIN-14 con verificador
  // inválido en ITF-14/GS1-128/DataMatrix.
  const digits = (raw.length === 8 && upcaExp.length === 12) ? upcaExp : raw
  const qty = Math.max(1, parseInt(payload.quantity, 10) || 1)
  const z = []
  z.push(`~SD${profile.darkness || 18}`)
  z.push('^XA')
  if (profile.utf8) z.push('^CI28')   // no en ZPL clásico (2844-Z)
  if (profile.dmEscape === '~') z.push(`^CT${CT_TEMP}`)   // `~` pasa como dato (ver CT_TEMP)
  z.push('^PR2')
  z.push('^PW800')
  z.push('^LL800')
  z.push('^LH0,0')

  let y = 150
  if (payload.productName) { z.push(centeredText(y, 28, T(payload.productName), true)); y += 44 }

  const BAR_H = 180  // altura de barras 1D (~22 mm)
  // Centrado: x = (800 - ancho estimado del símbolo) / 2
  const center = w => Math.max(20, Math.round((800 - w) / 2))

  switch (symbology) {
    case 'ean13': {
      // ^BE: 12 dígitos, la impresora calcula el verificador (módulo 3 dots)
      const body = digits.slice(-13).slice(0, 12)
      z.push(`^BY3,3,${BAR_H}`)
      z.push(`^FO${center(95 * 3)},${y}^BEN,${BAR_H},Y,N^FD${body}^FS`)
      y += BAR_H + 60
      break
    }
    case 'ean8': {
      const body = digits.slice(-8).slice(0, 7)
      z.push(`^BY3,3,${BAR_H}`)
      z.push(`^FO${center(67 * 3)},${y}^B8N,${BAR_H},Y,N^FD${body}^FS`)
      y += BAR_H + 60
      break
    }
    case 'upca': {
      const body = digits.slice(-12).slice(0, 11)
      z.push(`^BY3,3,${BAR_H}`)
      z.push(`^FO${center(95 * 3)},${y}^BUN,${BAR_H},Y,N,Y^FD${body}^FS`)
      y += BAR_H + 60
      break
    }
    case 'upce': {
      // ^B9 recibe los 10 dígitos centrales del UPC-A expandido (sin NS ni verificador)
      const upca = String(payload.upcaExpansion || '').replace(/\D/g, '')
      if (upca.length !== 12) return null
      z.push(`^BY3,3,${BAR_H}`)
      z.push(`^FO${center(51 * 3)},${y}^B9N,${BAR_H},Y,N,Y^FD${upca.slice(1, 11)}^FS`)
      y += BAR_H + 60
      break
    }
    case 'itf14': {
      // Interleaved 2/5 con los 14 dígitos + BARRAS PORTADORAS (bearer bars,
      // exigidas por el estándar ITF-14 — el marco del símbolo)
      const body = digits.padStart(14, '0').slice(0, 14)
      const module = 3
      const wide = module * 2.5
      const w = Math.round(7 * (2 * (2 * wide + 3 * module)) + 4 * module + wide + module)
      const x = center(w)
      const bearer = 12
      z.push(`^FO${x - 20},${y - bearer}^GB${w + 40},${BAR_H + 2 * bearer + 30},${bearer}^FS`)
      z.push(`^BY${module},2.5,${BAR_H}`)
      z.push(`^FO${x},${y}^B2N,${BAR_H},Y,N,N^FD${body}^FS`)
      y += BAR_H + 2 * bearer + 60
      break
    }
    case 'gs1-128': {
      const data = parenToFNC1(payload.elementString || `(01)${digits.padStart(14, '0')}`)
      const dataFNC1 = FNC1 + data
      let module = 3
      if (code128WidthDots(dataFNC1, module) > 760) module = 2
      z.push(`^BY${module},3,${BAR_H - 40}`)
      z.push(`^FO${center(code128WidthDots(dataFNC1, module))},${y}^BCN,${BAR_H - 40},Y,N,N^FD${zplBcField(dataFNC1)}^FS`)
      y += BAR_H + 40
      break
    }
    case 'gs1datamatrix': {
      // ^BX con carácter de escape del perfil ('_' moderno / '~' clásico):
      // <esc>1 = FNC1 (inicial y separadores); el escape literal en los datos
      // se codifica como su hex (_5F / ~7E)
      const e = profile.dmEscape
      const data = parenToFNC1(payload.elementString || `(01)${digits.padStart(14, '0')}`)
      const bxData = e + '1' + data.split(e).join(e + (e === '_' ? '5F' : '7E')).replace(/\u001d/g, e + '1')
      // Módulo 8 dots = X 1,0 mm; tamaño real del símbolo para centrar y
      // reservar el alto justo (antes se asumían 26 módulos fijos)
      const module = 8
      const side = dataMatrixSide(FNC1 + data)
      z.push(`^FO${center(side * module)},${y}^BXN,${module},200,,,,${e}^FD${bxData}^FS`)
      y += side * module + 30
      // El DataMatrix no lleva línea de interpretación: texto aparte
      z.push(centeredText(y, 20, (payload.elementString || `(01)${digits.padStart(14, '0')}`)))
      y += 34
      break
    }
    case 'databarexpandedstacked': {
      // ^BR tipo 6 (Expandido) recibe los AIs SIN paréntesis (artículo Zebra
      // 000026216: ^BRN,6,3,8,100,4^FD010800439500018017230215) — con "(01)"
      // literal la impresora codificaba OTRO código (visto en la TLP2844-Z).
      // Separador 8 = apilado expandido; 10 segmentos/fila = 2 filas anchas.
      // ^BR no admite FNC1/delimitador: un AI de largo VARIABLE solo puede ir
      // AL FINAL (si el usuario reordenó (10) al medio → null → navegador).
      const es = payload.elementString || `(01)${digits.padStart(14, '0')}`
      // Preferido: gráfico exacto generado por bwip-js (databarZpl.js) —
      // cualquier orden de AIs, filas balanceadas, idéntico a la vista previa
      // payload.databarBars = función (x0, y0) → { zpl, widthDots, heightDots }
      // (databarZplBars con posición absoluta: rectángulos ^GB, compatible
      // con todo firmware). payload.databarGraphic = ^GFA (alternativa).
      if (typeof payload.databarBars === 'function') {
        const probe = payload.databarBars(0, 0)
        if (probe && probe.zpl) {
          const g = payload.databarBars(center(probe.widthDots), y)
          z.push(g.zpl)
          y += g.heightDots + 24
          z.push(centeredText(y, 20, es))
          y += 34
          break
        }
      }
      const g = payload.databarGraphic
      if (g && g.zpl) {
        z.push(`^FO${center(g.widthDots)},${y}${g.zpl}^FS`)
        y += g.heightDots + 24
        z.push(centeredText(y, 20, es))
        y += 34
        break
      }
      const parts = es.split(/\((\d{2,4})\)/).filter(Boolean)
      const ais = []
      for (let i = 0; i < parts.length; i += 2) ais.push({ ai: parts[i], value: parts[i + 1] || '' })
      if (ais.some((p, i) => !isFixedAI(p.ai) && i < ais.length - 1)) return null
      const data = ais.map(p => p.ai + p.value).join('')
      z.push(`^FO${center(Math.round((24.5 * 10 + 4) * 3))},${y}^BRN,6,3,8,80,10^FD${data}^FS`)
      y += 240
      z.push(centeredText(y, 20, es))
      y += 34
      break
    }
    default:
      return null // GS1 QR: sin soporte ZPL confiable — impresión por navegador
  }

  y += 14
  z.push(centeredText(y, 22, `GTIN: ${digits.padStart(14, '0')}`))
  z.push(`^PQ${qty}`)
  if (profile.dmEscape === '~') z.push('^CT~')   // restaurar el prefijo de control
  z.push('^XZ')
  return z.join('\n')
}
